import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/use-toast';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Briefcase, 
  Building2, 
  Camera, 
  Lock, 
  ShieldCheck, 
  History,
  LogOut,
  Edit2,
  Trash2
} from 'lucide-react';

const ProfilePage = () => {
  const { user, api, logout } = useAuth(); // ✅ استخدم api من context
  
  // States
  const [profile, setProfile] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    phone: '',
    department: '',
    address: '',
    bio: '',
    avatar: null
  });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [loginHistory, setLoginHistory] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // ✅ Load profile on mount
  useEffect(() => {
    loadProfile();
  }, []);

  // ✅ Load Profile from API
  const loadProfile = async () => {
    try {
      console.log('📡 Loading profile...');
      
      const response = await api.get('/profile/get-profile.php');
      
      console.log('✅ Profile response:', response);
      
      if (response.success) {
        setProfile(response.data);
        
        // Load login history
        if (response.loginHistory && response.loginHistory.length > 0) {
          setLoginHistory(response.loginHistory);
        } else {
          const localHistory = JSON.parse(localStorage.getItem('loginHistory') || '[]')
            .filter(h => h.email === response.data.email)
            .slice(0, 5);
          setLoginHistory(localHistory);
        }
      }
    } catch (error) {
      console.error('❌ Failed to load profile:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to load profile',
        variant: 'destructive'
      });
    }
  };

  // ✅ Initialize form when modal opens
  useEffect(() => {
    if (profile && isEditModalOpen) {
      setEditForm({
        name: profile.name || '',
        phone: profile.phone || '',
        department: profile.department || '',
        address: profile.address || '',
        bio: profile.bio || '',
        avatar: profile.avatar || null
      });
    }
  }, [profile, isEditModalOpen]);

  // ✅ Handle Profile Update
  const handleProfileUpdate = async () => {
    if (!editForm.name.trim()) {
      toast({ 
        title: "Error", 
        description: "Name is required", 
        variant: "destructive" 
      });
      return;
    }

    try {
      setIsLoading(true);
      
      console.log('📝 Updating profile:', editForm);
      
      // Send data without avatar
      const { avatar, ...profileData } = editForm;
      
      const response = await api.put('/profile/update-profile.php', profileData);
      
      console.log('✅ Update response:', response);
      
      if (response.success) {
        setProfile(response.data);
        setIsEditModalOpen(false);
        
        toast({ 
          title: "Success", 
          description: "Profile updated successfully",
          className: "bg-green-50 text-green-900 border-green-200"
        });
      }
    } catch (error) {
      console.error('❌ Update error:', error);
      toast({ 
        title: "Error", 
        description: error.message || 'Failed to update profile', 
        variant: "destructive" 
      });
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ Handle Avatar Upload
  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      // Validate file size
      if (file.size > 10 * 1024 * 1024) {
        toast({ 
          title: "Error", 
          description: "Image size too large. Max 10MB.", 
          variant: "destructive" 
        });
        return;
      }
      
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        toast({ 
          title: "Error", 
          description: "Invalid file type. Only images allowed.", 
          variant: "destructive" 
        });
        return;
      }
      
      setIsUploading(true);
      
      console.log('📤 Uploading avatar:', file.name, file.size);
      
      // ✅ Get token from localStorage
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      // ✅ Upload using FormData
      const formData = new FormData();
      formData.append('avatar', file);

      const response = await fetch('https://new.sbdemolition.online/api/profile/upload-avatar.php', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();
      
      console.log('✅ Upload response:', data);
      
      if (data.success) {
        // Update form and profile
        const newAvatarUrl = data.avatar_url || data.avatarUrl;
        setEditForm({ ...editForm, avatar: newAvatarUrl });
        setProfile({ ...profile, avatar: newAvatarUrl });
        
        toast({ 
          title: "Success", 
          description: "Avatar uploaded successfully",
          className: "bg-green-50 text-green-900 border-green-200"
        });
      } else {
        throw new Error(data.error || 'Upload failed');
      }
    } catch (error) {
      console.error('❌ Avatar upload error:', error);
      toast({ 
        title: "Error", 
        description: error.message || 'Failed to upload avatar', 
        variant: "destructive" 
      });
    } finally {
      setIsUploading(false);
    }
  };

  // ✅ Handle Delete Avatar
  const handleDeleteAvatar = async () => {
    if (!window.confirm('Are you sure you want to delete your avatar?')) return;
    
    try {
      setIsUploading(true);
      
      console.log('🗑️ Deleting avatar...');
      
      const response = await api.post('/profile/delete-avatar.php');
      
      console.log('✅ Delete response:', response);
      
      if (response.success) {
        setEditForm({ ...editForm, avatar: null });
        setProfile({ ...profile, avatar: null });
        
        toast({ 
          title: "Success", 
          description: "Avatar deleted successfully",
          className: "bg-green-50 text-green-900 border-green-200"
        });
      }
    } catch (error) {
      console.error('❌ Delete error:', error);
      toast({ 
        title: "Error", 
        description: error.message || 'Failed to delete avatar', 
        variant: "destructive" 
      });
    } finally {
      setIsUploading(false);
    }
  };

  // ✅ Handle Password Change
  const handlePasswordChange = async () => {
    const { currentPassword, newPassword, confirmPassword } = passwordForm;

    if (!currentPassword || !newPassword || !confirmPassword) {
      toast({ 
        title: "Error", 
        description: "All fields are required", 
        variant: "destructive" 
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      toast({ 
        title: "Error", 
        description: "New passwords do not match", 
        variant: "destructive" 
      });
      return;
    }

    if (passwordStrength < 3) {
      toast({ 
        title: "Weak Password", 
        description: "Please choose a stronger password", 
        variant: "warning" 
      });
      return;
    }

    try {
      setIsLoading(true);
      
      console.log('🔒 Changing password...');
      
      const response = await api.post('/profile/change-password.php', {
        currentPassword,
        newPassword
      });
      
      console.log('✅ Password change response:', response);
      
      if (response.success) {
        setIsPasswordModalOpen(false);
        setPasswordForm({ 
          currentPassword: '', 
          newPassword: '', 
          confirmPassword: '' 
        });
        
        toast({ 
          title: "Success", 
          description: "Password changed successfully", 
          className: "bg-green-50 text-green-900 border-green-200"
        });
      }
    } catch (error) {
      console.error('❌ Password change error:', error);
      toast({ 
        title: "Error", 
        description: error.message || 'Failed to change password', 
        variant: "destructive" 
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Password Strength Calculator
  const checkPasswordStrength = (pass) => {
    let strength = 0;
    if (pass.length >= 8) strength++;
    if (/[A-Z]/.test(pass)) strength++;
    if (/[0-9]/.test(pass)) strength++;
    if (/[^A-Za-z0-9]/.test(pass)) strength++;
    setPasswordStrength(strength);
  };

  // ✅ Use profile data (fallback to user from context)
  const displayData = profile || user;

  if (!displayData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-600">Loading profile...</div>
      </div>
    );
  }

  return (
    <>
      <Helmet><title>My Profile - Stone Beam</title></Helmet>
      
      <div className="space-y-6 animate-in fade-in duration-500 max-w-5xl mx-auto pb-10">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-navy-steal">My Profile</h1>
          <Button variant="outline" className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200" onClick={logout}>
            <LogOut className="w-4 h-4 mr-2" /> Sign Out
          </Button>
        </div>

        {/* Header Section */}
        <Card className="border-t-4 border-t-navy-steal shadow-md">
          <CardContent className="p-8">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="relative group shrink-0">
                <Avatar className="w-32 h-32 border-4 border-white shadow-lg ring-2 ring-stone-100">
                  <AvatarImage src={displayData.avatar} className="object-cover" />
                  <AvatarFallback className="text-3xl bg-navy-steal text-white font-bold">
                    {displayData.name?.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div 
                  className="absolute bottom-0 right-0 bg-safety-orange text-white p-2 rounded-full cursor-pointer hover:bg-safety-orange-hover shadow-md transition-transform hover:scale-110"
                  onClick={() => setIsEditModalOpen(true)}
                >
                  <Camera className="w-4 h-4" />
                </div>
              </div>
              
              <div className="text-center md:text-left flex-1 space-y-2">
                <div>
                  <h2 className="text-3xl font-bold text-gray-900">{displayData.name}</h2>
                  <p className="text-lg text-safety-orange font-medium">{displayData.role}</p>
                </div>
                <p className="text-stone-500 max-w-xl">{displayData.bio || "No bio added yet."}</p>
                
                <div className="flex flex-wrap justify-center md:justify-start gap-3 mt-4">
                  <Button onClick={() => setIsEditModalOpen(true)} className="bg-navy-steal hover:bg-navy-steal/90">
                    <Edit2 className="w-4 h-4 mr-2" /> Edit Profile
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Contact Info */}
          <Card className="md:col-span-2 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-xl">
                <User className="w-5 h-5 text-safety-orange" /> Personal Information
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-1">
                  <Label className="text-stone-500 text-xs uppercase tracking-wide">Email Address</Label>
                  <div className="flex items-center gap-2 font-medium text-stone-900 bg-stone-50 p-2 rounded border border-stone-100">
                    <Mail className="w-4 h-4 text-stone-400" />
                    {displayData.email}
                    <span className="text-xs ml-auto bg-stone-200 px-1.5 py-0.5 rounded text-stone-600">Read-Only</span>
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-stone-500 text-xs uppercase tracking-wide">Phone Number</Label>
                  <div className="flex items-center gap-2 font-medium text-stone-900 p-2">
                    <Phone className="w-4 h-4 text-stone-400" />
                    {displayData.phone || 'Not set'}
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-stone-500 text-xs uppercase tracking-wide">Department</Label>
                  <div className="flex items-center gap-2 font-medium text-stone-900 p-2">
                    <Building2 className="w-4 h-4 text-stone-400" />
                    {displayData.department || 'Not set'}
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-stone-500 text-xs uppercase tracking-wide">Job Title</Label>
                  <div className="flex items-center gap-2 font-medium text-stone-900 p-2">
                    <Briefcase className="w-4 h-4 text-stone-400" />
                    {displayData.role}
                  </div>
                </div>
                <div className="md:col-span-2 space-y-1">
                  <Label className="text-stone-500 text-xs uppercase tracking-wide">Address</Label>
                  <div className="flex items-center gap-2 font-medium text-stone-900 p-2">
                    <MapPin className="w-4 h-4 text-stone-400" />
                    {displayData.address || 'Not set'}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Security & Settings */}
          <div className="space-y-6">
            <Card className="shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-xl">
                  <ShieldCheck className="w-5 h-5 text-safety-orange" /> Security
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button variant="outline" className="w-full justify-start" onClick={() => setIsPasswordModalOpen(true)}>
                  <Lock className="w-4 h-4 mr-2" /> Change Password
                </Button>
                <div className="pt-4 border-t border-stone-100">
                  <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                    <History className="w-4 h-4 text-stone-400" /> Recent Logins
                  </h4>
                  {loginHistory.length > 0 ? (
                    <div className="space-y-3">
                      {loginHistory.map((log, idx) => (
                        <div key={idx} className="text-xs flex justify-between items-center text-stone-500">
                          <span>{new Date(log.timestamp).toLocaleDateString()} {new Date(log.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                          <span className="text-green-600 bg-green-50 px-1.5 rounded">{log.status}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-stone-400 italic">No history available yet.</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Edit Profile Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Edit Profile</DialogTitle>
            <DialogDescription>
              Make changes to your profile information here.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-6 py-4">
            {/* Avatar Upload Section */}
            <div className="flex justify-center mb-4">
              <div className="relative group">
                <Avatar className="w-24 h-24 border-2 border-stone-200">
                  <AvatarImage src={editForm.avatar} className="object-cover" />
                  <AvatarFallback className="text-xl">{editForm.name?.charAt(0)}</AvatarFallback>
                </Avatar>
                
                {/* Upload Overlay */}
                <label 
                  htmlFor="avatar-upload"
                  className="absolute inset-0 flex items-center justify-center bg-black/40 text-white opacity-0 group-hover:opacity-100 rounded-full cursor-pointer transition-opacity"
                >
                  {isUploading ? (
                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent"></div>
                  ) : (
                    <Camera className="w-6 h-6" />
                  )}
                  <input 
                    id="avatar-upload" 
                    type="file" 
                    accept="image/*" 
                    className="hidden" 
                    onChange={handleAvatarUpload}
                    disabled={isUploading}
                  />
                </label>
                
                {/* Delete Button */}
                {editForm.avatar && (
                  <button
                    type="button"
                    onClick={handleDeleteAvatar}
                    disabled={isUploading}
                    className="absolute -bottom-2 -right-2 bg-red-500 hover:bg-red-600 text-white p-1.5 rounded-full shadow-md transition-transform hover:scale-110 disabled:opacity-50"
                    title="Delete avatar"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name">Full Name</Label>
                <Input id="edit-name" value={editForm.name} onChange={(e) => setEditForm({...editForm, name: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-phone">Phone Number</Label>
                <Input id="edit-phone" value={editForm.phone} onChange={(e) => setEditForm({...editForm, phone: e.target.value})} placeholder="+971..." />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-dept">Department</Label>
                <Input id="edit-dept" value={editForm.department} onChange={(e) => setEditForm({...editForm, department: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-role">Role (Read Only)</Label>
                <Input id="edit-role" value={displayData.role} disabled className="bg-stone-50" />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-address">Address</Label>
              <Input id="edit-address" value={editForm.address} onChange={(e) => setEditForm({...editForm, address: e.target.value})} placeholder="City, Country" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-bio">Bio</Label>
              <Textarea id="edit-bio" value={editForm.bio} onChange={(e) => setEditForm({...editForm, bio: e.target.value})} placeholder="Tell us a little about yourself" className="resize-none h-24" />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditModalOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleProfileUpdate} 
              className="bg-navy-steal hover:bg-navy-steal/90" 
              disabled={isUploading || isLoading}
            >
              {isLoading ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Password Modal */}
      <Dialog open={isPasswordModalOpen} onOpenChange={setIsPasswordModalOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
            <DialogDescription>
              Ensure your account is secure with a strong password.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="current-pass">Current Password</Label>
              <Input 
                id="current-pass" 
                type="password" 
                value={passwordForm.currentPassword} 
                onChange={(e) => setPasswordForm({...passwordForm, currentPassword: e.target.value})} 
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="new-pass">New Password</Label>
              <Input 
                id="new-pass" 
                type="password" 
                value={passwordForm.newPassword} 
                onChange={(e) => {
                  setPasswordForm({...passwordForm, newPassword: e.target.value});
                  checkPasswordStrength(e.target.value);
                }} 
              />
              {passwordForm.newPassword && (
                <div className="space-y-1">
                  <div className="flex gap-1 h-1.5 mt-2">
                    <div className={`flex-1 rounded-full ${passwordStrength >= 1 ? 'bg-red-500' : 'bg-stone-200'}`}></div>
                    <div className={`flex-1 rounded-full ${passwordStrength >= 2 ? 'bg-orange-500' : 'bg-stone-200'}`}></div>
                    <div className={`flex-1 rounded-full ${passwordStrength >= 3 ? 'bg-yellow-500' : 'bg-stone-200'}`}></div>
                    <div className={`flex-1 rounded-full ${passwordStrength >= 4 ? 'bg-green-500' : 'bg-stone-200'}`}></div>
                  </div>
                  <p className="text-[10px] text-stone-500 text-right">
                    {passwordStrength < 2 ? 'Weak' : passwordStrength < 4 ? 'Medium' : 'Strong'}
                  </p>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm-pass">Confirm New Password</Label>
              <Input 
                id="confirm-pass" 
                type="password" 
                value={passwordForm.confirmPassword} 
                onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})} 
              />
            </div>
            
            <div className="text-xs text-stone-500 bg-stone-50 p-2 rounded">
              <p>Requirements:</p>
              <ul className="list-disc pl-4 mt-1 space-y-0.5">
                <li>Min 8 characters</li>
                <li>Uppercase & Lowercase</li>
                <li>Numbers & Special characters</li>
              </ul>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsPasswordModalOpen(false)}>Cancel</Button>
            <Button 
              onClick={handlePasswordChange} 
              className="bg-navy-steal hover:bg-navy-steal/90" 
              disabled={passwordStrength < 3 || isLoading}
            >
              {isLoading ? 'Updating...' : 'Update Password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ProfilePage;
